import ButtonBack from 'components/FormControls/back-button'
import renderRadioGroup from 'components/FormControls/radioGroup'
import renderInputField from 'components/FormControls/textInput'
import { Button, Typography } from 'gu-ui-kit'
import formStyles from 'modules/Forms/common-form.module.scss'
import PropTypes from 'prop-types'
import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Field, formValueSelector, reduxForm } from 'redux-form'
import validator from 'utils/asyncValidator'
import emailValidator from 'utils/validators/emailValidator'
import * as Yup from 'yup'
import pick from 'lodash/pick'
import { ROUTES } from 'constants/routes'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import { useHistory } from 'react-router'

const validationSchema = Yup.object().shape({
  type: Yup.string().required('agency::forms.add_client.validation_messages.required_select'),
  email: Yup.string().when('type', {
    is: (val) => val !== 'create_by_name',
    then: (schema) =>
      schema
        .test(
          'emailValidator',
          'common::validation_messages.incorrect_email_format',
          emailValidator,
        )
        .required('common::validation_messages.required'),
  }),
  name: Yup.string().required('common::validation_messages.required'),
})

let AgencyClientRequestForm = ({ handleSubmit, submitting, form }) => {
  const history = useHistory()
  const pathGen = useGeneratePathI18n()
  const { t } = useTranslation()
  const selector = formValueSelector(form)
  const { type, registeredFields } = useSelector((state) => ({
    type: selector(state, 'type'),
    registeredFields: state['form'][form].registeredFields,
  }))

  const submit = useCallback(
    (values, dispatch) =>
      new Promise((resolve, reject) =>
        dispatch({
          type: 'AGENCY/REQUEST_ADD',
          meta: { resolve, reject },
          payload: {
            data: pick(values, Object.keys(registeredFields)),
            formName: form,
          },
        }),
      ).then(() => history.push(pathGen(ROUTES.AGENCY_REQUESTS))),
    [type, registeredFields],
  )

  return (
    <div className={formStyles['formContainer']}>
      <div className={formStyles['formContainer__inner']}>
        <div className={'margin-bottom-40'}>
          <Typography
            variant={'Header-50'}
            component={'h1'}
            className={'margin-bottom-8'}
          >
            {t('agency::forms.add_client.title')}
          </Typography>
          <Typography
            variant={'Body-15'}
            color={'Label-Secondary'}
            component={'p'}
          >
            {t('agency::forms.add_client.description')}
          </Typography>
        </div>

        <form onSubmit={handleSubmit(submit)}>
          <div className={'margin-bottom-32'}>
            <Field
              name="type"
              t={t}
              component={renderRadioGroup}
              options={[
                {
                  value: 'create',
                  label: t('agency::forms.add_client.fields.type.options.create.label'),
                },
                {
                  value: 'create_by_name',
                  label: t('agency::forms.add_client.fields.type.options.create_by_name.label'),
                },
              ]}
            />
          </div>

          <div className={'margin-bottom-40'}>
            {type !== 'create_by_name' && (
              <div className={'margin-bottom-16'}>
                <Field
                  fullWidth
                  name="email"
                  type="email"
                  component={renderInputField}
                  t={t}
                  label={t(`agency::forms.add_client.fields.email.label`, {
                    context: type,
                  })}
                />
              </div>
            )}

            <div className={'margin-bottom-16'}>
              <Field
                fullWidth
                name="name"
                type="text"
                component={renderInputField}
                label={t('agency::forms.add_client.fields.name.label')}
                t={t}
              />
            </div>
          </div>
          <div className={formStyles['formControl__action-buttons']}>
            <ButtonBack onGoBack={() => history.push(pathGen(ROUTES.AGENCY_CLIENTS))} />

            <Button
              disabled={submitting}
              size="l"
              variant="primary"
              type="submit"
            >
              {t('common::buttons.create')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

AgencyClientRequestForm = reduxForm({
  form: 'AgencyClientRequestForm',
  initialValues: {
    type: 'create',
    email: '',
  },
  asyncValidate: validator(validationSchema),
})(AgencyClientRequestForm)

AgencyClientRequestForm.propTypes = {
  handleSubmit: PropTypes.func,
  submitting: PropTypes.bool,
  array: PropTypes.object,
  autofill: PropTypes.func,
  form: PropTypes.string,
}

export default AgencyClientRequestForm
